/**
 * MediaLibrarySelector Component
 * Modal for selecting media assets from the library to attach to communications
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Search,
  Image,
  Video,
  File,
  Music,
  Link as LinkIcon,
  Check,
  RefreshCw,
  Folder
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface MediaAsset {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  asset_type: string;
  mime_type?: string;
  file_size?: number;
  external_url?: string;
  thumbnail_url?: string;
  short_url?: string;
  is_public: boolean;
}

interface MediaLibrarySelectorProps {
  onSelect: (media: MediaAsset) => void;
  onClose: () => void;
  selectedIds: number[];
  maxSelection?: number;
  allowedTypes?: string[]; // 'image', 'video', 'document', 'audio', 'web_link'
}

const ASSET_TYPE_ICONS: Record<string, React.ElementType> = {
  document: File,
  image: Image,
  video: Video,
  audio: Music,
  web_link: LinkIcon
};

const ASSET_TYPE_COLORS: Record<string, string> = {
  document: 'text-blue-500 bg-blue-100',
  image: 'text-green-500 bg-green-100',
  video: 'text-purple-500 bg-purple-100',
  audio: 'text-orange-500 bg-orange-100',
  web_link: 'text-cyan-500 bg-cyan-100'
};

export const MediaLibrarySelector: React.FC<MediaLibrarySelectorProps> = ({
  onSelect,
  onClose,
  selectedIds,
  maxSelection = 4,
  allowedTypes
}) => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [generatingUrls, setGeneratingUrls] = useState<Set<number>>(new Set());

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (selectedType) params.append('asset_type', selectedType);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '50');

      const response = await authFetch(`${getWeaveinApiUrl()}/api/media/assets?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        let assetList = data.assets || data;

        // Filter by allowed types if specified
        if (allowedTypes && allowedTypes.length > 0) {
          assetList = assetList.filter((a: MediaAsset) => allowedTypes.includes(a.asset_type));
        }

        setAssets(assetList);
      }
    } catch (err) {
      console.error('Error fetching media assets:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedType, searchQuery, allowedTypes]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const generateShortUrl = async (asset: MediaAsset) => {
    if (asset.short_url) {
      // Already has a short URL, just select it
      onSelect(asset);
      return;
    }

    setGeneratingUrls(prev => new Set(prev).add(asset.id));

    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/media/links`, {
        method: 'POST',
        jsonBody: {
            asset_id: asset.id,
            link_type: 'preview',
            is_public: true
          }
      });

      if (response.ok) {
        const linkData = await response.json();
        // Update the asset with the new short URL
        const updatedAsset = {
          ...asset,
          short_url: linkData.full_url
        };

        // Update in the list
        setAssets(prev => prev.map(a => a.id === asset.id ? updatedAsset : a));

        // Select the asset
        onSelect(updatedAsset);
      } else {
        // If link generation fails, still select the asset without short URL
        onSelect(asset);
      }
    } catch (err) {
      console.error('Error generating short URL:', err);
      onSelect(asset);
    } finally {
      setGeneratingUrls(prev => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    }
  };

  const handleSelect = (asset: MediaAsset) => {
    if (selectedIds.includes(asset.id)) {
      // Deselect
      onSelect(asset);
    } else if (selectedIds.length < maxSelection) {
      // Generate short URL if needed and select
      generateShortUrl(asset);
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Select Media from Library
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedIds.length}/{maxSelection} items selected
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Type filter */}
          <select
            value={selectedType || ''}
            onChange={(e) => setSelectedType(e.target.value || null)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">All Types</option>
            {(!allowedTypes || allowedTypes.includes('image')) && <option value="image">Images</option>}
            {(!allowedTypes || allowedTypes.includes('video')) && <option value="video">Videos</option>}
            {(!allowedTypes || allowedTypes.includes('document')) && <option value="document">Documents</option>}
            {(!allowedTypes || allowedTypes.includes('audio')) && <option value="audio">Audio</option>}
            {(!allowedTypes || allowedTypes.includes('web_link')) && <option value="web_link">Web Links</option>}
          </select>

          <button
            onClick={fetchAssets}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
              <Folder className="w-12 h-12 mb-2 opacity-50" />
              <p>No media assets found</p>
              <p className="text-sm">Upload files to your media library first</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {assets.map((asset) => {
                const TypeIcon = ASSET_TYPE_ICONS[asset.asset_type] || File;
                const colorClass = ASSET_TYPE_COLORS[asset.asset_type] || 'text-gray-500 bg-gray-100';
                const isSelected = selectedIds.includes(asset.id);
                const isGenerating = generatingUrls.has(asset.id);
                const canSelect = isSelected || selectedIds.length < maxSelection;

                return (
                  <div
                    key={asset.id}
                    onClick={() => canSelect && handleSelect(asset)}
                    className={`relative bg-white dark:bg-gray-700 rounded-lg border-2 overflow-hidden transition-all cursor-pointer ${
                      isSelected
                        ? 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-800'
                        : canSelect
                        ? 'border-gray-200 dark:border-gray-600 hover:border-purple-300'
                        : 'border-gray-200 dark:border-gray-600 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}

                    {/* Generating indicator */}
                    {isGenerating && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
                        <RefreshCw className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}

                    {/* Thumbnail */}
                    <div className="aspect-video bg-gray-100 dark:bg-gray-600 flex items-center justify-center">
                      {asset.thumbnail_url ? (
                        <img
                          src={asset.thumbnail_url}
                          alt={asset.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`p-4 rounded-full ${colorClass}`}>
                          <TypeIcon className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate" title={asset.name}>
                        {asset.name}
                      </h4>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {asset.asset_type}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(asset.file_size)}
                        </span>
                      </div>
                      {asset.short_url && (
                        <div className="mt-1 flex items-center gap-1">
                          <LinkIcon className="w-3 h-3 text-green-500" />
                          <span className="text-xs text-green-600 dark:text-green-400">Short URL ready</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Click to select media. Short URLs will be generated automatically.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrarySelector;
